// API服务层 - 封装所有后端API调用
// 后端地址: https://bazi.tryonebest.com

import { api } from "./api_core";
import { baziApi } from "./api_bazi";
import { liuyaoApi } from "./api_liuyao";
import { qimenApi } from "./api_qimen";
import { zeriApi } from "./api_zeri";
import { yunyingApi } from "./api_yunying";

// API服务对象
export const apiService = {
  // 用户注册
  // POST /api/register
  register: async (userData) => {
    try {
      const response = await api.post("/register", userData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "注册失败",
      };
    }
  },

  // 用户登录
  // POST /api/login
  login: async (credentials) => {
    try {
      const response = await api.post("/login", credentials);
      if (response.data.access_token) {
        // 保存token到本地存储
        localStorage.setItem("access_token", response.data.access_token);

        // 保存token过期时间 (24小时后)
        const expiresAt = new Date().getTime() + 24 * 60 * 60 * 1000;
        localStorage.setItem("token_expires_at", expiresAt);

        // 保存用户信息
        if (response.data.user) {
          localStorage.setItem("user", JSON.stringify(response.data.user));
        }

        return {
          success: true,
          token: response.data.access_token,
          user: response.data.user,
        };
      }
      return { success: false, error: "登录失败" };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "登录失败",
      };
    }
  },

  // 获取当前用户信息
  getCurrentUser: async () => {
    try {
      const response = await api.get("/me");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // 检查用户是否已登录
  isAuthenticated: () => {
    const token = localStorage.getItem("access_token");
    return !!token;
  },

  // 获取当前用户信息 (Alias)
  getMe: async () => {
    try {
      const response = await api.get("/me");
      return response.data;
    } catch (error) {
      return null;
    }
  },

  // 获取公开服务列表
  getPublicServices: async () => {
    const response = await api.get("/services/public");
    return response.data;
  },

  // 获取评论列表
  // GET /api/public/<biz_type>/<chart_id>/comments
  getComments: async (bizType, chartId) => {
    try {
      const response = await api.get(`/public/${bizType}/${chartId}/comments`);
      return { success: true, data: response.data.data || [] };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "获取评论失败",
        data: []
      };
    }
  },

  // 提交评论
  // POST /api/public/<biz_type>/<chart_id>/comments
  postComment: async (bizType, chartId, content, parentId = null) => {
    try {
      const response = await api.post(`/public/${bizType}/${chartId}/comments`, {
        content,
        parent_id: parentId
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "提交评论失败",
      };
    }
  },

  // 获取占题类型列表 (公共服务)
  getZhantiTypes: async () => {
    try {
      const response = await api.get("/liuyao/zhanti_types");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch zhanti types", error);
      return [];
    }
  },

  // 模拟支付
  simulatePayment: async (data) => {
    const response = await api.post("/payment/simulate", data);
    return response.data;
  },

  // 切换点赞状态 (通用接口)
  // POST /api/public/<biz_type>/<chart_id>/like
  toggleLike: async (bizType, chartId) => {
    try {
      const response = await api.post(`/public/${bizType}/${chartId}/like`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "操作失败",
      };
    }
  },

  // 切换收藏状态 (通用接口)
  // POST /api/public/<biz_type>/<chart_id>/favorite
  toggleFavorite: async (bizType, chartId) => {
    try {
      const response = await api.post(`/public/${bizType}/${chartId}/favorite`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "操作失败",
      };
    }
  },

  // 获取用户对卦例的互动状态 (通用接口)
  // GET /api/public/<biz_type>/chart/<chart_id>/interaction-status
  getInteractionStatus: async (bizType, chartId) => {
    try {
      const response = await api.get(`/public/${bizType}/chart/${chartId}/interaction-status`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "获取状态失败",
      };
    }
  },

  // 关注/取消关注用户 (通用接口)
  // POST /api/public/user/<user_id>/follow
  toggleFollow: async (userId) => {
    try {
      const response = await api.post(`/public/user/${userId}/follow`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "操作失败",
      };
    }
  },

  // 获取关注状态 (通用接口)
  // GET /api/public/user/<user_id>/follow-status
  getFollowStatus: async (userId) => {
    try {
      const response = await api.get(`/public/user/${userId}/follow-status`);
      return { success: true, isFollowing: response.data.is_following };
    } catch (error) {
      return { success: false, isFollowing: false };
    }
  },

  // 获取我的关注列表 (通用接口)
  // GET /api/public/my-follows
  getMyFollows: async (page = 1, perPage = 20) => {
    try {
      const response = await api.get(`/public/my-follows?page=${page}&per_page=${perPage}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "获取关注列表失败",
        data: { follows: [], total: 0, page: 1, pages: 0 }
      };
    }
  },

  // 获取用户资料 (通用接口)
  // GET /api/public/user/<user_id>/profile
  getUserProfile: async (userId) => {
    try {
      const response = await api.get(`/public/user/${userId}/profile`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "获取用户资料失败",
      };
    }
  },

  // 获取用户发布统计 (通用接口)
  // GET /api/public/user/<user_id>/publications-stats
  getUserPublicationsStats: async (userId) => {
    try {
      const response = await api.get(`/public/user/${userId}/publications-stats`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "获取统计失败",
      };
    }
  },

  // 获取用户发布列表 (通用接口)
  // GET /api/public/user/<user_id>/publications
  getUserPublications: async (userId, page = 1, perPage = 20) => {
    try {
      const response = await api.get(`/public/user/${userId}/publications?page=${page}&per_page=${perPage}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "获取发布列表失败",
        data: { publications: [], total: 0, page: 1, pages: 0 }
      };
    }
  },

  // 聚合其他模块
  ...baziApi,
  ...liuyaoApi,
  ...qimenApi,
  ...zeriApi,
  ...yunyingApi,
};

export default apiService;
